import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { MainLayout } from "../layout/MainLayout";
import { AlertCircle, Loader2, Edit2, Trash2 } from "lucide-react";
import Card from "../common/Card";
import SectionHeader from "../common/SectionHeader";
import FileUploadZone from "../uploads/FileUploadZone";
import FileTable from "../uploads/FileTable";
import PrimaryButton from "../ui/PrimaryButton";
import Toast from "../common/Toast";
import ExcelPreview from "../utils/ExcelPreviewCrrem";
import useCrremScenarios from "../hooks/useCrremScenarios";

/**
 * Loading screen component
 */
const LoadingScreen = () => (
  <div className="flex items-center justify-center min-h-screen">
    <Loader2 className="h-12 w-12 animate-spin text-blue-600" />
  </div>
);

/**
 * Error screen component
 */
const ErrorScreen = ({ message }) => (
  <div className="flex flex-col items-center justify-center min-h-screen">
    <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
    <p className="text-red-600">Error: {message}</p>
  </div>
);

/**
 * Empty state component
 */
const EmptyState = ({ message }) => (
  <div className="text-center py-12 bg-gray-50 rounded-lg">
    <p className="text-gray-600">{message || "No data available."}</p>
  </div>
);

/**
 * Rename dialog component
 */
const RenameDialog = ({ scenario, onConfirm, onCancel }) => {
  const [newName, setNewName] = useState(scenario);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-md">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Rename Scenario
        </h3>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            New Name
          </label>
          <input
            type="text"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            autoFocus
          />
        </div>

        <div className="flex justify-end space-x-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm(newName)}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Rename
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * CRREM Component for managing CRREM scenarios
 */
const CrremComponent = () => {
  // Get project ID from URL parameters
  const { projectId } = useParams();

  // Local state
  const [toast, setToast] = useState(null);
  const [renamingScenario, setRenamingScenario] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [showExcelPreview, setShowExcelPreview] = useState(false);

  // Use custom hook for CRREM scenarios with projectId from URL
  const {
    scenarios,
    loading,
    isProcessing,
    error,
    selectedScenarios,
    handleSelectScenario,
    handleSelectAllScenarios,
    deleteScenarios,
    uploadScenario,
    renameScenario,
  } = useCrremScenarios(projectId);

  /**
   * Show toast notification
   */
  const showToast = (message, type = "success") => {
    setToast({ message, type });
  };

  /**
   * Handle file selection
   */
  const handleFilesSelect = (files) => {
    if (files.length === 0) return;
    setSelectedFile(files[0]);

    // Show Excel preview when a file is selected
    setShowExcelPreview(true);
  };

  /**
   * Handle scenario deletion
   */
  const handleDeleteScenarios = async () => {
    if (selectedScenarios.length === 0) return;

    const confirmDelete = window.confirm(
      `Are you sure you want to delete ${selectedScenarios.length} scenario(s)?`
    );

    if (confirmDelete) {
      const result = await deleteScenarios();
      showToast(result.message, result.success ? "success" : "error");
    }
  };

  /**
   * Handle Excel preview upload
   */
  const handleExcelUpload = async (sheetConfigs) => {
    try {
      const successfulUploads = [];
      const failedUploads = [];

      // Upload each selected sheet as a separate scenario
      for (const sheet of sheetConfigs) {
        try {
          // Create FormData for this specific sheet
          const formData = new FormData();
          formData.append("file", selectedFile);
          formData.append("sheet_name", sheet.originalName);
          formData.append("scenario_name", sheet.newName);

          // Upload this scenario
          const result = await uploadScenario(
            sheet.newName,
            selectedFile,
            sheet.originalName
          );

          if (result.success) {
            successfulUploads.push(sheet.newName);
          } else {
            failedUploads.push({ name: sheet.newName, error: result.message });
          }
        } catch (err) {
          failedUploads.push({ name: sheet.newName, error: err.message });
        }
      }

      // Create result message
      let message = "";
      if (successfulUploads.length > 0) {
        message += `Successfully uploaded ${
          successfulUploads.length
        } scenario(s): ${successfulUploads.join(", ")}. `;
      }

      if (failedUploads.length > 0) {
        message += `Failed to upload ${failedUploads.length} scenario(s).`;
      }

      // Show toast
      showToast(message, failedUploads.length === 0 ? "success" : "warning");

      // Reset state
      setSelectedFile(null);
      setShowExcelPreview(false);
    } catch (err) {
      showToast(`Error uploading scenarios: ${err.message}`, "error");
    }
  };

  /**
   * Handle Excel preview cancel
   */
  const handleExcelCancel = () => {
    setSelectedFile(null);
    setShowExcelPreview(false);
  };

  /**
   * Handle rename dialog open
   */
  const handleRenameClick = () => {
    if (selectedScenarios.length !== 1) {
      showToast("Please select exactly one scenario to rename", "error");
      return;
    }
    setRenamingScenario(selectedScenarios[0]);
  };

  /**
   * Handle rename confirmation
   */
  const handleRenameConfirm = async (newName) => {
    const result = await renameScenario(renamingScenario, newName);
    showToast(result.message, result.success ? "success" : "error");
    setRenamingScenario(null);
  };

  if (loading) {
    return (
      <MainLayout>
        <LoadingScreen />
      </MainLayout>
    );
  }

  if (error) {
    return (
      <MainLayout>
        <ErrorScreen message={error} />
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      {/* Toast notification */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      {/* Rename dialog */}
      {renamingScenario && (
        <RenameDialog
          scenario={renamingScenario}
          onConfirm={handleRenameConfirm}
          onCancel={() => setRenamingScenario(null)}
        />
      )}

      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card
            title={`CRREM Scenarios - Project ${projectId}`}
            subtitle="Manage Carbon Risk Real Estate Monitor scenarios for decarbonization analysis"
          >
            <div className="space-y-6 p-6">
              {/* Manage Scenarios Section */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="p-6">
                  <SectionHeader
                    title="Manage Scenarios"
                    action={
                      selectedScenarios.length > 0 && (
                        <div className="flex space-x-2">
                          {selectedScenarios.length === 1 && (
                            <PrimaryButton
                              onClick={handleRenameClick}
                              className="bg-blue-600 hover:bg-blue-700"
                              width="auto"
                            >
                              <div className="flex items-center">
                                <Edit2 className="w-4 h-4 mr-1" />
                                Rename
                              </div>
                            </PrimaryButton>
                          )}
                          <PrimaryButton
                            onClick={handleDeleteScenarios}
                            isLoading={isProcessing}
                            loadingText="Deleting..."
                            className="bg-red-600 hover:bg-red-700"
                            width="auto"
                          >
                            <div className="flex items-center">
                              <Trash2 className="w-4 h-4 mr-1" />
                              Delete ({selectedScenarios.length})
                            </div>
                          </PrimaryButton>
                        </div>
                      )
                    }
                  />

                  {scenarios.length > 0 ? (
                    <FileTable
                      files={scenarios.map((scenario) => ({
                        id: scenario,
                        name: scenario,
                        type: "CRREM Scenario",
                        _created_at: null,
                        _updated_at: null,
                      }))}
                      selectedFiles={selectedScenarios}
                      onSelectFile={handleSelectScenario}
                      onSelectAll={handleSelectAllScenarios}
                    />
                  ) : (
                    <EmptyState message="No CRREM scenarios available. Upload a new scenario to get started." />
                  )}
                </div>
              </div>

              {/* Upload New Scenario Section */}
              {!showExcelPreview ? (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                  <div className="p-6">
                    <SectionHeader title="Upload New Scenario" />

                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Excel File
                        </label>
                        <FileUploadZone
                          onFilesSelect={handleFilesSelect}
                          disabled={isProcessing}
                          accept=".xlsx,.xls"
                          acceptedTypes={["XLSX", "XLS"]}
                        />
                      </div>

                      {/* Required Columns Info */}
                      <div className="bg-blue-50 p-4 rounded-md">
                        <div className="flex">
                          <div className="flex-shrink-0">
                            <AlertCircle className="h-5 w-5 text-blue-400" />
                          </div>
                          <div className="ml-3">
                            <h3 className="text-sm font-medium text-blue-800">
                              Required Excel Format
                            </h3>
                            <div className="mt-2 text-sm text-blue-700">
                              <p>
                                Your Excel file should include the following
                                columns:
                              </p>
                              <ul className="list-disc pl-5 mt-1">
                                <li>Year</li>
                                <li>Country_specific_Energy_Target</li>
                                <li>Baseline_Emission</li>
                                <li>Decarbonization_Target_Scenario_1</li>
                                <li>Asset_Energy_Intensity</li>
                                <li>
                                  Climate_And_Grid_Corrected_Asset_Performance
                                </li>
                              </ul>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                  <div className="p-6">
                    <SectionHeader title="Configure Excel Sheets" />

                    <ExcelPreview
                      file={selectedFile}
                      onCancel={handleExcelCancel}
                      onUpload={handleExcelUpload}
                    />
                  </div>
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>
    </MainLayout>
  );
};

export default CrremComponent;
